import React, { useState } from "react";
import { ChevronDown, HelpCircle, SearchX, MessageSquare } from "lucide-react";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "components/ui/collapsible";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { HELP_CATEGORIES, FAQS } from "../../helpContent";

const FaqSection = ({ searchQuery, selectedCategory, onSelectCategory, onClearSearch, onScrollToContact }) => {
  // Track open state for each FAQ
  const [openIds, setOpenIds] = useState(() => new Set(["faq-1"]));

  const toggleFaq = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Filter FAQs based on category and search query
  const query = (searchQuery || "").trim().toLowerCase();
  const filteredFaqs = FAQS.filter((item) => {
    // Category match
    const categoryMatch = selectedCategory === "all" || item.category === selectedCategory;
    if (!categoryMatch) return false;

    // Search query match
    if (!query) return true;
    const qMatch = item.question.toLowerCase().includes(query);
    const aMatch = item.answer.toLowerCase().includes(query);
    const tagMatch = item.tags?.some((t) => t.toLowerCase().includes(query));
    return qMatch || aMatch || tagMatch;
  });

  return (
    <section id="faq-section" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-gray-900">Frequently Asked Questions</h2>
          <p className="text-sm text-gray-500">
            Common questions regarding methodologies, metrics, exporting, and platform administration.
          </p>
        </div>

        {searchQuery && (
          <span className="text-xs text-gray-500 bg-gray-100 px-2.5 py-1 rounded-md self-start sm:self-auto">
            Found {filteredFaqs.length} {filteredFaqs.length === 1 ? "result" : "results"} for "{searchQuery}"
          </span>
        )}
      </div>

      {/* Category Pills */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
        {HELP_CATEGORIES.map((cat) => {
          const isSelected = selectedCategory === cat.id;
          return (
            <button
              key={cat.id}
              onClick={() => onSelectCategory(cat.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-150 border ${
                isSelected
                  ? "bg-gray-900 text-white border-gray-900 shadow-sm"
                  : "bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
              }`}
            >
              {cat.label}
            </button>
          );
        })}
      </div>

      {/* FAQ Items */}
      {filteredFaqs.length > 0 ? (
        <div className="space-y-3">
          {filteredFaqs.map((faq) => {
            const isOpen = openIds.has(faq.id);

            return (
              <Collapsible
                key={faq.id}
                open={isOpen}
                onOpenChange={() => toggleFaq(faq.id)}
                className="border border-gray-200 bg-white rounded-xl overflow-hidden transition-colors hover:border-gray-300"
              >
                <CollapsibleTrigger asChild>
                  <button className="w-full flex items-center justify-between p-4 sm:p-5 text-left gap-4 hover:bg-gray-50/50 transition-colors">
                    <div className="flex items-start gap-3">
                      <HelpCircle size={18} className="text-blue-500 shrink-0 mt-0.5" />
                      <span className="font-medium text-sm sm:text-base text-gray-900 leading-snug">
                        {faq.question}
                      </span>
                    </div>
                    <ChevronDown
                      size={18}
                      className={`text-gray-400 shrink-0 transition-transform duration-200 ${
                        isOpen ? "transform rotate-180 text-gray-700" : ""
                      }`}
                    />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="px-4 pb-4 sm:px-5 sm:pb-5 pt-0 text-gray-600 text-sm leading-relaxed border-t border-gray-100 mt-2 pt-3">
                    <p className="whitespace-pre-line">{faq.answer}</p>
                    {faq.tags && faq.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3 pt-2">
                        {faq.tags.map((tag) => (
                          <Badge
                            key={tag}
                            variant="secondary"
                            className="text-[10px] bg-gray-100 text-gray-500 hover:bg-gray-200 cursor-default"
                          >
                            #{tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            );
          })}
        </div>
      ) : (
        /* Empty State */
        <div className="text-center py-12 px-4 rounded-xl border border-dashed border-gray-300 bg-white space-y-4">
          <div className="w-12 h-12 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center mx-auto">
            <SearchX size={24} />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-gray-900">No matching questions found</p>
            <p className="text-xs text-gray-500 max-w-sm mx-auto">
              We couldn't find any FAQs matching your query. You can try adjusting your search terms or send us a message directly.
            </p>
          </div>
          <div className="flex items-center justify-center gap-3 pt-1">
            {searchQuery && (
              <Button variant="outline" size="sm" onClick={onClearSearch} className="text-xs">
                Clear Search
              </Button>
            )}
            <Button size="sm" onClick={onScrollToContact} className="text-xs gap-1.5 bg-blue-600 hover:bg-blue-700">
              <MessageSquare size={14} />
              Ask Support Team
            </Button>
          </div>
        </div>
      )}
    </section>
  );
};

export default FaqSection;
